/**
 * PsicoSalud — Inicio de sesión del profesional.
 *
 * Split-panel: a la izquierda el claim editorial sobre fondo verde-teal;
 * a la derecha el formulario. Los selectores data-cy se conservan porque
 * las pruebas E2E referencian por ellos, no por la composición visual.
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import LanguageSwitcher from '@/components/LanguageSwitcher';
import LoginParticles from '@/components/LoginParticles';
import { useAuth } from '@/context/AuthContext';

export default function LoginPage() {
  const { t } = useTranslation();
  const { login, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!isLoading && isAuthenticated) {
    const from = (location.state as { from?: string } | null)?.from ?? '/';
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      navigate('/', { replace: true });
    } catch {
      setError(t('login.error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <aside className="login-side" aria-label="Identidad del sistema">
        <LoginParticles />
        <div className="login-side-marca">
          {t('app.name')}
          <span className="marca-pulso">.</span>
        </div>

        <p className="login-side-claim">{t('app.tagline')}</p>

        <div className="login-side-meta">
          <div className="t-mayuscula" style={{ opacity: 0.7, marginBottom: '0.5rem' }}>
            {t('login.subtitle')}
          </div>
          <div style={{ fontFamily: 'var(--fuente-mono)', fontSize: '0.78rem', opacity: 0.55 }}>
            v0.1 · UCSA 2026
          </div>
        </div>
      </aside>

      <section className="login-formulario" aria-label="Formulario de acceso">
        <div className="login-formulario-inner">
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginBottom: 'var(--espacio-6)',
            }}
          >
            <LanguageSwitcher />
          </div>

          <span
            className="t-mayuscula"
            style={{ color: 'var(--principal)', display: 'block', marginBottom: 'var(--espacio-1)' }}
          >
            {t('login.title')}
          </span>
          <h2 style={{ marginBottom: 'var(--espacio-2)' }}>{t('login.title')}</h2>
          <p className="subtitulo">{t('login.subtitle')}</p>

          {error && (
            <div className="notice notice-error" role="alert" data-cy="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="field">
              <label htmlFor="email">{t('login.email')}</label>
              <input
                id="email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                data-cy="login-email"
              />
            </div>

            <div className="field">
              <label htmlFor="password">{t('login.password')}</label>
              <div className="login-password-wrap">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  data-cy="login-password"
                  style={{ paddingRight: '2.75rem' }}
                />
                <button
                  type="button"
                  className="login-password-toggle"
                  onClick={() => setShowPassword((value) => !value)}
                  aria-label={
                    showPassword
                      ? t('login.passwordHide')
                      : t('login.passwordShow')
                  }
                  aria-pressed={showPassword}
                  aria-controls="password"
                  title={
                    showPassword
                      ? t('login.passwordHide')
                      : t('login.passwordShow')
                  }
                  data-cy="login-password-toggle"
                >
                  {showPassword ? (
                    <svg
                      viewBox="0 0 24 24"
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
                      <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
                      <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
                      <line x1="2" x2="22" y1="2" y2="22" />
                    </svg>
                  ) : (
                    <svg
                      viewBox="0 0 24 24"
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              style={{ width: '100%', marginTop: 'var(--espacio-2)' }}
              data-cy="login-submit"
            >
              {submitting ? t('login.submitting') : t('login.submit')}
            </button>
          </form>

          <p className="ayuda">{t('login.privacy')}</p>
        </div>
      </section>
    </div>
  );
}
